/**
 * The managed-login-flow rules, as pure functions — the `auth.flow.*`
 * counterpart to auth.ts, and shared for the same reason: a widget author builds
 * against the mock host and ships against the real bridge, so anything both
 * hosts have to agree on lives here rather than in either one.
 *
 * What is *not* here: anything that performs the flow. Starting the
 * authorization request, holding the Keycloak cookie jar and the opaque
 * continuation URL, following the final redirect, exchanging the code and
 * signing the customer in are host mechanics by definition — the widget never
 * sees any of them (see the auth flow section of contracts.ts).
 */

import { WidgetAuthFlowState, WidgetAuthFlowStep } from './contracts';

/**
 * The step types this version of the contract describes — the ones documented
 * on `WidgetAuthFlowStep.metadata` and in the authoring guide.
 *
 * It is **not** the set a project can produce. The flow is configured in
 * Keycloak, so a realm can start returning a step no build of this SDK has heard
 * of; that is why `WidgetAuthFlowStep.type` is an open string and why the rule
 * below is fail-closed rather than exhaustive-match.
 */
export const AUTH_FLOW_KNOWN_STEP_TYPES = [
  'username_password',
  'username',
  'password',
  'pin',
  'sms_otp',
  'email_otp',
  'totp',
  'totp_setup',
  'phone_number',
  'update_password',
  // The FINx1 default flow (docs/platform/authentication/default-login-flow.md), proven by
  // tools/login-flow-harness on 2026-08-30: `identify` is the extension's username form
  // (finxone-auth-username-form), `create_pin` / `reset_pin` are pin-authenticator's set and
  // recovery steps, `verify_identity` is user-attributes-authenticator (date of birth + postcode).
  'identify',
  'create_pin',
  'reset_pin',
  'verify_identity',
  // Tier 1b. Purely additive, and additive in both directions: the rule below is
  // fail-closed, so a build that predates this entry renders the step as a dead end
  // rather than guessing at it. The gateway reads the same list — `publicStep` in
  // `app-session.service.ts` rejects a step type this array does not carry — so the
  // entry is also what lets the step reach an application at all.
  'device_signature',
] as const;

export type WidgetAuthFlowKnownStepType = (typeof AUTH_FLOW_KNOWN_STEP_TYPES)[number];

/**
 * Whether this SDK version describes the step type.
 *
 * **The fail-closed rule.** `false` means render a dead end — an explanatory
 * message plus a restart (and any alternative the step itself lists) — and
 * nothing else. Never fall back to "render `fields` generically and hope": the
 * fields of an unknown step are unknown by construction, so a generic render is
 * a text box a customer types a credential into against a step whose meaning the
 * widget could not establish. A dead end costs one login attempt; a guess can
 * cost the credential.
 */
export function isKnownAuthFlowStepType(type: string): type is WidgetAuthFlowKnownStepType {
  return (AUTH_FLOW_KNOWN_STEP_TYPES as readonly string[]).includes(type);
}

/** Narrows a flow state to the step case, so a widget branches on `status` once. */
export function isAuthFlowStep(state: WidgetAuthFlowState): state is WidgetAuthFlowStep {
  return state.status === 'interaction-required';
}

/**
 * The message both hosts reject an unoffered alternative with.
 *
 * Shared because the rule is shared: only the names in the current step's
 * `actions` may be submitted. Sending one the step did not offer is a widget
 * bug — the server answers `action_not_available` for it — and both hosts say so
 * with the same sentence rather than letting the mock be the lenient one.
 */
export function authFlowActionUnavailableMessage(operation: string, action: string): string {
  return (
    `${operation}: '${action}' is not one of the current step's actions. Submit only names the ` +
    'step lists in `actions` — the alternatives available on a step are decided by the flow, not ' +
    'by the widget.'
  );
}

/**
 * The message both hosts reject a submit with when no flow is in progress.
 * `auth.flow.start` mints the transaction; there is nothing to submit to before
 * it, and nothing to resume after a `cancel` or a completion.
 */
export function authFlowNotStartedMessage(operation: string): string {
  return (
    `${operation}: no login flow is in progress. Call auth.flow.start first — a transaction that ` +
    'has been completed or cancelled cannot be resumed.'
  );
}
